import { Box, useTheme, IconButton, ButtonBase } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { tokens } from "../../../../../theme";
import Shbutton from "../../../../theme/buttons/Shbutton";
import ShLabel from "../../../../theme/label/ShLabel";
import Text from "../../../../theme/text/Text";

import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import ButtonGroup from "../../../../theme/buttons/ButtonGroup";

import { useTranslation } from "react-i18next";
import { useGetOrderLinesQuery, useUpdateOrderLinesMutation, useGetShipmentLabelMutationMutation } from "../../../../../newapi/order/ordersSlice";
import { showToastMessageRequestError, showToastMessageUpdateRequest } from "../../../../../utils/toasts";
import OrderLinesCard from "../../../cards/orders/OrderLinesCard";
import ShDataGrid2 from "../../../../theme/datagrid2/ShDataGrid2";
import ShLabelSkeleton from "../../../../theme/skeleton/ShLabelSkeleton";
import ShTextSkeleton from "../../../../theme/skeleton/ShTextSkeleton";
// import SPStockDetails from "../../SpStockDetails/SPStockDetails";
import { getOrderStateData } from "../../../../../utils/labelColorText";
import SidepanelLoader from "../../../../theme/sidepanel2/SidepanelLoader";
import { useSelector } from "react-redux";
import Subheading from "../../../../theme/text/Subheading";
import Heading from "../../../../theme/text/Heading";
import SHQuickAction from "../../../../theme/Modal/SHQuickAction";
import { formatPrice } from '../../../../../utils/helpers';
import { ENV_TYPES } from "../../../Sidebar";
import { OrderlinesComp } from "../components/OrderlinesComp";
import { OrderEvents } from "../components/OrderEvents";
import ShipmentsStack, { stackableShipments } from "../components/ShipmentsStack";
import { booleanValueTypes } from "../../../../Streams/Conditions/ConditionSettings";
import { useGetProductsSelectQuery } from '../../../../../newapi/warehouse/productSlice';
import ShValidatedInputDropdown from '../../../../theme/dropdowns/ShValidatedInputDropdown';
import NumberInput from "../../../../theme/inputs/NumberInput";
import SpDesktopFooter from '../../../../theme/sidepanel2/SidepanelFooter';
import { toast } from 'react-toastify';
import { general_states } from "../../../../../utils/staticEnums";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../../utils/permissions";
import MSelectLabel from "../../../Modals/MSelectLabel";
import { useGetLogsQuery } from "../../../../../newapi/global/logsSlice";
import { TimeLine } from "./Summary";

// Slimmed timeline for the relation portal: user/line/status changes only (pick
// actions and internal events are excluded). Shipments are rendered separately in
// the timeline from the real shipment records so their afgemeld/synced state shows.
const RELATION_TIMELINE_ACTIONS = ["update"];

const SpStockDetails = SidepanelLoader(() => import("../../SpStockDetails/SPStockDetails"));
const SpReturnDetails = SidepanelLoader(() => import("../../SpReturnDetails/SpReturnDetails"));

const EditViewActions = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    return (
        <ButtonGroup version={2}>
            <Shbutton
                className={"group-btn"}
                color="grey"
                variant="outlined"
                onClick={props.onCancel}
                disabled={props.isLoading}
            >
                {t("cancel")}
            </Shbutton>
            <Shbutton
                className={"group-btn"}
                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                variant="contained"
                onClick={props.onSave}
                disabled={props.isLoading}
                isLoading={props.isLoading}
            >
                {t("save")}
            </Shbutton>
        </ButtonGroup>
    )
}

const OrderLinesEditView = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);

    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = selectedEnvironmentType === ENV_TYPES[200];
    const selectedId = useSelector((state) => state.settings.environment.selectedId);

    const { data: products } = useGetProductsSelectQuery({relationId: isRelation ? selectedId : props.data?.relationId, addStock: true},
        { skip: !isRelation && !props.data?.relationId }
    );

    // Fetch existing order lines
    const { data: existingLinesData, isLoading: isLoadingLines } = useGetOrderLinesQuery({
        args: {
            id: props.data?.id,
            page: 1,
            size: 1000,
            body: {}
        }
    }, {
        skip: !props.data?.id
    });

    const [updateOrderLines, { isLoading: isSaving }] = useUpdateOrderLinesMutation();

    const [productOptions, setProductOptions] = useState([]);
    const [newOrderLines, setNewOrderLines] = useState([{
        sku: "",
        amount: 0,
        index: 1
    }]);

    useEffect(() => {
        if (products) {
            const mappedProducts = products.map(product => ({
                id: product.sku,
                name: product.combined,
                barcodes:product.barcodes,
            }));
            setProductOptions(mappedProducts);
        }
    }, [products]);

    // Initialize with existing order lines from API
    useEffect(() => {
        if (existingLinesData?.data && existingLinesData.data.length > 0) {
            // Filter to only include top-level orderlines (parentProductId: null) and exclude add-ons
            // These are the only ones that can be edited
            const topLevelLines = existingLinesData.data.filter(line =>
                (line.parentProductId === null || line.parentProductId === undefined) &&
                line.type !== 'add_on'
            );

            const linesCopy = topLevelLines.map((line, index) => ({
                sku: line.product?.sku || line.sku || "",
                amount: line.amount || 0,
                index: index + 1
            }));

            linesCopy.push({
                sku: "",
                amount: 0,
                index: linesCopy[linesCopy.length - 1].index + 1
            });

            setNewOrderLines(linesCopy);
        } else if (!isLoadingLines && props.data?.id) {
            // If there are no existing lines, start with one empty line
            setNewOrderLines([{
                sku: "",
                amount: 0,
                index: 1
            }]);
        }
    }, [existingLinesData, isLoadingLines, props.data?.id]);

    const handleChange = (index, key, value) => {
        const orderLinesCopy = [...newOrderLines];
        const lineToUpdate = orderLinesCopy.find(line => line.index === index);

        if (lineToUpdate) {
            lineToUpdate[key] = value;

            if (orderLinesCopy[orderLinesCopy.length - 1].sku.length > 0) {
                orderLinesCopy.push({
                    sku: "",
                    amount: 0,
                    index: orderLinesCopy[orderLinesCopy.length - 1].index + 1
                });
            }

            setNewOrderLines(orderLinesCopy);
        }
    };

    const handleProductSelect = (key, val) => {
        const lineIndex = parseInt(key.split(".")[0]);
        const lineKey = key.split(".")[1];

        handleChange(lineIndex, lineKey, val);
    }

    const handleDeleteLineRow = (index) => {
        const updatedOrderLines = newOrderLines.filter(line => line.index !== index);
        setNewOrderLines(updatedOrderLines);
    }

    // A line can only be removed when it is a real line (not the empty add-row) and at
    // least one other real line remains, so an order can never end up without lines.
    const realLineCount = newOrderLines.filter(line => line.sku.length > 0).length;
    const canDeleteLine = (orderLine) => orderLine.sku.length > 0 && realLineCount > 1;

    const handleCancel = useCallback(() => {
        // Switch back to view mode without saving
        if (props.setConfig) {
            props.setConfig(config => ({
                ...config,
                is_editting: null,
            }));
        }
    }, [props]);

    const handleSave = useCallback(async () => {
        const mappedOrderLines = newOrderLines
            .filter(x => x.sku.length > 0 && x.amount > 0)
            .map(x => ({
                sku: x.sku,
                amount: x.amount,
            }));

        try {
            const response = await updateOrderLines({
                orderId: props.data.id,
                body: mappedOrderLines
            }).unwrap();

            showToastMessageUpdateRequest(t, response);

            // Switch back to the summary view
            if (props.setConfig) {
                props.setConfig(config => ({
                    ...config,
                    is_editting: null,
                }));
            }

            // Refresh the order data
            if (props.onSave) {
                props.onSave();
            }
        } catch (error) {
            toast.error(t('failed_to_update_order_lines'), {
                position: toast.POSITION.TOP_RIGHT
            });
        }
    }, [newOrderLines, updateOrderLines, props, t]);

    // Set up mobile actions
    useEffect(() => {
        if (props.replaceView) {
            props.replaceView({
                ...props.config,
                mobileActions: <EditViewActions onSave={handleSave} onCancel={handleCancel} isLoading={isSaving} />,
            });
        }

        return () => {
            // cleanup
        }
    }, [handleSave, isSaving, props]);

    // Show loading text in the main interface instead of blocking the entire view
    const isStillLoading = isLoadingLines && newOrderLines.length === 1 && !newOrderLines[0].sku;

    return (
        <Box height={1} display={"flex"} flexDirection={"column"} overflow={"auto"} paddingX={3} paddingY={3}
            sx={{
                "@media screen and (min-width: 48.1em)"  :{
                    marginBottom:11
                }
            }}
        >
            <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} mb={2}>
                <Text bold fontSize={18}>{t("edit_order_lines")}</Text>
                {isStillLoading && <Text light>{t("loading")}...</Text>}
            </Box>

            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    p: 2,
                    "@media screen and (max-width: 47.9375em)": {
                        p: 1
                    }
                }}
            >
                {newOrderLines.map((orderLine, index) => (
                    <React.Fragment key={"edit-order-line-" + index}>
                    <Box flex={1}
                        sx={{
                            display: "flex",
                            gap: 2,
                            justifyContent: "flex-start",
                            "@media screen and (max-width: 47.9375em)": {
                                backgroundColor: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                boxShadow: theme.palette.mode === "dark" ? "none" : "0px 2px 4px rgba(0, 0, 0, 0.1)",
                                border: theme.palette.mode === "dark" ? "none" : "1px solid " + colors.grey[100],
                                borderRadius: 4,
                                py: 1.5,
                                px: 1.5,
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 2,
                                overflow: "hidden",
                            }
                        }}
                    >
                        <Box
                            sx={{
                                width: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 0.5,
                                "@media screen and (max-width: 47.9375em)": {
                                    overflow: "hidden",
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    width: 40,
                                    flexShrink: 0,
                                }}
                            >
                                <Text light>{t("sku")}</Text>
                            </Box>
                            <ShValidatedInputDropdown
                                height={40}
                                tabIndex={-1}
                                key={"edit-orderLine-product-" + orderLine.index}
                                displayName={"name"}
                                changeField={"id"}
                                valueKey={"id"}
                                closeOnSelection
                                searchOptions={{
                                    enabled: true,
                                    placeholder: t("search_product_placeholder"),
                                    searchables: ["barcodes",'id']
                                }}
                                styles={{
                                    flex: 1,
                                    minWidth: 200,
                                    "@media screen and (max-width: 47.9375em)": {
                                        minWidth: "unset",
                                    }
                                }}
                                maxWidth={400}
                                disallowDeselection
                                name={`${orderLine.index}.sku`}
                                selected={productOptions?.find(item => item.id && item.id === orderLine.sku)?.id ?? -1}
                                options={productOptions ? productOptions : []}
                                noSelection={t("choose_option")}
                                onChange={handleProductSelect}
                            />
                            {canDeleteLine(orderLine) ?
                                <Box
                                    tabIndex={-1}
                                    sx={{
                                        width: 40,
                                        display: "none",
                                        justifyContent: "flex-end",
                                        "@media screen and (max-width: 47.9375em)": {
                                            display: "flex",
                                        }
                                    }}
                                >
                                    <IconButton tabIndex={-1} type="button" color="red" onClick={() => handleDeleteLineRow(orderLine.index)} sx={{p: 1}}>
                                        <DeleteIcon/>
                                    </IconButton>
                                </Box>
                                : null
                            }
                        </Box>
                        <Box
                            sx={{
                                width: 200,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 0.5,
                                "@media screen and (max-width: 47.9375em)": {
                                    width: "unset",
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    width: 50,
                                    flexShrink: 0,
                                }}
                            >
                                <Text light>{t("quantity")}</Text>
                            </Box>
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                }}
                            >
                                <NumberInput
                                    btnSize={"24"}
                                    inputSX={{
                                        width: 144,
                                        "@media screen and (max-width: 47.9375em)": {
                                            px: 1,
                                            flex: 1,
                                            width: "unset",
                                        }
                                    }}
                                    value={orderLine.amount}
                                    change={(e) => handleChange(orderLine.index, 'amount', e)}
                                />

                                {canDeleteLine(orderLine) ?
                                    <Box
                                        tabIndex={-1}
                                        sx={{
                                            width: 40,
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            "@media screen and (max-width: 47.9375em)": {
                                                display: "none",
                                            }
                                        }}
                                    >
                                        <IconButton tabIndex={-1} type="button" color="red" onClick={() => handleDeleteLineRow(orderLine.index)} sx={{p: 1}}>
                                            <DeleteIcon/>
                                        </IconButton>
                                    </Box>
                                    : <Box tabIndex={-1} width={40} sx={{
                                        "@media screen and (max-width: 47.9375em)": {
                                            display: "none",
                                        }
                                    }}/>
                                }
                            </Box>
                        </Box>
                    </Box>
                    </React.Fragment>
                ))}
            </Box>
            <SpDesktopFooter>
                <EditViewActions onSave={handleSave} onCancel={handleCancel} isLoading={isSaving} />
            </SpDesktopFooter>
        </Box>
    );
};

const gridOptions = {
    export:true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
    hide:true,


};

const OrderLines = (props) => {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [isExtended, setIsExtended] = useState(false);
    const [spStockLoading, setSpStockLoading] = useState();

    const [productId, setProductId] = useState();
    const productIdRef = useRef(productId);

    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = selectedEnvironmentType === ENV_TYPES[200];



    const [returnIsExtended, setReturnIsExtended] = useState(false);
    const [returnSpLoading, setReturnSpLoading] = useState();

    const [getShipmentLabel, { isLoading: shipmentLabelIsLoading }] = useGetShipmentLabelMutationMutation();
    const [labelsModal, setLabelsModal] = useState({ open: false, labels: [], type: "" });

    const [returnId, setReturnId] = useState();
    const returnIdRef = useRef(returnId);
    
    const getReturnDetails = (returnid) => {
    
        const newReturnId = parseInt(returnid);
        // Only update state if the productId has actually changed
        if (returnIdRef.current !== newReturnId) {
            if(!returnSpLoading) {
                setReturnSpLoading(true);
            }
            setReturnId(newReturnId);
            returnIdRef.current = newReturnId; // Update the ref to the new value
        }
        setReturnIsExtended(true);
    };

    const [config,setConfig] = useState({
        is_editting:null,
    });

    const hasInvoicePrivilege = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.RP_INVOICE_READ])
    );

    // Check if order is editable based on webshopcredentials and state
    // Editable if: (no webshopcredentials OR (not bol/shopify AND integrator null)) AND state in open/backorder/replenish
    const isOrderEditable = props.data && (
        (
            !props.data.webshopcredentials ||
            (
                (
                    props.data.webshopcredentials.webshop_type !== "bol" ||
                    props.data.deliveryMethod?.indexOf("vvb") === -1
                ) &&
                (props.data.webshopcredentials.integratorId === null || props.data.webshopcredentials.integratorId === 0)
            )
        )
        && [general_states.OPEN, general_states.BACKORDERED, general_states.REPLENISH].includes(props.data.state)
    );
    
    const getProductDetails = (params) => {
        if (!params) return;
    
        const newProductId = parseInt(params.product.id);
        // Only update state if the productId has actually changed
        if (productIdRef.current !== newProductId) {
            if(!spStockLoading){
                setSpStockLoading(true);
            }
            setProductId(newProductId);
            productIdRef.current = newProductId; // Update the ref to the new value
        }
            setIsExtended(true);
    };

    const toBoolean = value => {
        if (value === true || value === false) return value;      // already boolean
        if (value === "true") return true;
        if (value === "false") return false;
        return Boolean(value); // fallback for other cases
    };
    const { data: orderLinesData, isLoading: isLoadingLines } = useGetOrderLinesQuery({
        args: {
            id: props.orderId,
            page: 1,
            size: 1000,
            body: {}
        }
    }, {
        skip: !props.orderId
    });

    const { data: logData, refetch: refetchLogs } = useGetLogsQuery({
        args: {
            page: 1,
            size: 100,
            body: {
                type: "Order",
                externalId: props.data?.id
            }
        }
    }, {
        skip: !props.data?.id
    });

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions:gridOptions,
            afterHeader: (
                <Box>
                    <TimeLine
                        logData={logData}
                        isRelation
                        webshopType={props.data?.webshopcredentials?.webshop_type}
                    />
                </Box>
            )
        });

        return () => {
            // console.log("unmounting warehouse");
        }
    },[props.config,logData,props.data?.webshopcredentials?.webshop_type]);

    const hasReadPricePermission = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.RP_INVOICE_READ])
    );

    const orderTotals = useMemo(() => {
        if(!hasReadPricePermission) {
            return {
                sellingPrice:0,
                sellingPriceExVat:0,
            }
        }
        const lines = orderLinesData?.data ?? [];
        return lines.reduce((acc, line) => {
            acc.sellingPrice += Number(line.sellingPrice) || 0;
            acc.sellingPriceExVat += Number(line.sellingPriceExVAT) || 0;
            return acc;
        }, { sellingPrice: 0, sellingPriceExVat: 0 });
    }, [orderLinesData]);
    
    // If order is editable and edit view is active, show edit view
    if (config.is_editting !== null && isOrderEditable) {
        return (
            <OrderLinesEditView
                data={props.data}
                config={config}
                setConfig={setConfig}
                replaceView={props.replaceView}
                onSave={() => {
                    // The order data refreshes via RTK Query cache invalidation; the logs are
                    // refetched explicitly so the new update log appears in the timeline immediately.
                    refetchLogs();
                }}
            />
        );
    }

    const sortedShipments = stackableShipments(props.data?.shipments);

    return (
        <Box
            sx={{
                height:1,
                display:"flex",
                "@media screen and (max-width: 47.9375em)" : {
                    flexDirection:"column"
                }
            }}
        >
            <Box
                sx={{
                    flex:2,
                    "@media screen and (max-width: 47.9375em)" : {
                        width:1,
                    }
                }}
            >
            {(props.data?.linkedCosts && props.data?.linkedCosts.length > 0) && hasInvoicePrivilege &&
                <Box
                    sx={{
                        px:3,
                        pt:3,
                    }}
                >
                    
                        <SHQuickAction
                        maxWidth={560}
                            component={
                                <Box
                                    sx={{
                                        display:"flex",
                                        flexDirection:'column',
                                        pt:2,
                                        px:2,
                                    }}
                                >   
                                    <>
                                        <Box
                                            sx={{
                                                position:'relative',
                                                display:"flex",
                                                alignItems:"center",
                                                gap:3,
                                            }}
                                        >
                                            <Text
                                                light
                                                sx={{width: "120px"}}
                                            >
                                                {t("invoice")}
                                            </Text>
                                            <Box
                                            
                                                sx={{
                                                    width:"180px",
                                                    display:"flex",
                                                    alignItems:"center",
                                                    borderRadius:4,
                                                    "&:hover" : {
                                                        background:colors.grey[200],
                                                        cursor:"pointer",
                                                    },
                                                    "@media screen and (max-width: 768px)" : {
                                                        // px:0.5,
                                                    },
                                                }}
                                            >
                                                <Text light>{t("description")}</Text>
                                            </Box>
                                            <Text
                                                light
                                                sx={{width: "80px"}}
                                            >
                                                {t("amount")}
                                            </Text>
                                            <Text
                                                light
                                                sx={{width: "80px"}}
                                            >
                                                {t("price")}
                                            </Text>
                                            <Text
                                                light
                                                sx={{width: "80px"}}
                                            >
                                                {t("total")}
                                            </Text>
                                        </Box>

                                        {props.data?.linkedCosts.map((linked,index) => (
                                            <Box key={index+"pcs"}
                                                sx={{
                                                    position:'relative',
                                                    minHeight:40,
                                                    py:1,
                                                    display:"flex",
                                                    alignItems:"center",
                                                    gap:3,
                                                }}
                                            >
                                                <Text
                                                sx={{width: "120px"}}
                                                >
                                                    {t("invoice")} {linked.invoice}
                                                </Text>
                                                <Box

                                                    sx={{
                                                        width:"180px",
                                                        display:"flex",
                                                        alignItems:"center",
                                                        borderRadius:4,
                                                        "&:hover" : {
                                                            background:colors.grey[200],
                                                            cursor:"pointer",
                                                        },
                                                        "@media screen and (max-width: 768px)" : {
                                                            // px:0.5,
                                                        },
                                                    }}
                                                >
                                                    <Text
                                                        sx={{
                                                            wordBreak: "break-word",
                                                            overflowWrap: "break-word",
                                                        }}
                                                    >
                                                        {linked.description}
                                                    </Text>
                                                </Box>
                                                <Text
                                                sx={{width: "80px"}}
                                                >
                                                    {parseFloat(linked.amount)}
                                                </Text>
                                                <Text
                                                sx={{width: "80px"}}
                                                >
                                                    {formatPrice(i18n, linked.price)}
                                                </Text>
                                                <Text
                                                bold
                                                sx={{width: "80px"}}
                                                >
                                                    {formatPrice(i18n, parseFloat(linked.amount * linked.price))}
                                                </Text>
                                            </Box>
                                        ))}
                                    </>
                                </Box>
                            }
                        >
                        {({anchorEl,onMouseEnter,onMouseLeave,onTouchStart}) => (
                            <Box
                                aria-owns={anchorEl ? 'simple-popover' : undefined}
                                aria-haspopup="true"
                                onMouseEnter={onMouseEnter}
                                onMouseLeave={onMouseLeave}
                                onTouchStart={onTouchStart} 
                                sx={{
                                    background: theme.palette.mode === "dark" ? colors.grey[100] : colors.grey[50],
                                    p:1.5,
                                    borderRadius:5,
                                    display:"flex",
                                    justifyContent:'space-between',
                                    alignItems:"center"
                                }}
                            >
                                <Subheading light>
                                    {t("total_price")}
                                </Subheading>
                                <Heading>{formatPrice(i18n, props.data?.totalCost)}</Heading>
                            </Box>
                        )}
                    </SHQuickAction>
                    
                </Box>
            }

            <Box
                sx={{
                    mt:3,
                    px:3,
                    display:"flex",
                    "@media screen and (max-width: 47.9375em)" : {
                        mr:0,
                    }
                }}
            >
                <Box
                    sx={{
                        flex:1,
                        display:"flex",
                        flexDirection:"column",
                    }}
                >
                    <Subheading>{t("ordered_by")}</Subheading>
                    <Box
                        sx={{
                            flex:1,
                            mt:1,
                            p:2,
                            borderRadius:4,
                            display:"flex",
                            flexDirection:"column",
                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[50]
                        }}
                    >
                        <Text bold variant={theme.palette.mode === "dark" ? "blue" : "primary"}>{props.data?.deliveryName}</Text>
                        <Box
                            sx={{
                                mt:2,
                                display:"flex",
                                flexDirection:'column',
                            }}
                        >
                            {props.data?.customerReference?.length > 0 && (
                                <Box sx={{mb: 1, mt:1, display:"flex", flexDirection:'column'}}>
                                    <Text bold>{t("customer_reference")}:</Text>
                                    <Text>{props.data.customerReference}</Text>
                                </Box>
                            )}
                            <Text bold>{t("address")}:</Text>
                            <Text>{props.data?.shippingAddress?.addressLine1}</Text>
                            <Text light>{props.data?.shippingAddress?.postalCode}, {props.data?.shippingAddress?.city}</Text>
                            <Text light>{(props.data?.shippingAddress?.state && props.data?.shippingAddress?.state !== null) && `${props.data?.shippingAddress?.state}, `}{props.data?.shippingAddress?.country}</Text>
                        </Box>
                    </Box>
                </Box>
                {sortedShipments.length > 0 &&
                    <Box
                        sx={{
                            flex:1,
                            ml:1,
                            display:'flex',
                            flexDirection:'column',
                        }}
                    >
                        <Subheading>{t("shipments")}</Subheading>
                        <ShipmentsStack
                            shipments={sortedShipments}
                            // spShipments is the third tab in the relation tab config
                            onOpenShipments={() => props.setActiveChild?.(2)}
                        />
                    </Box>
                }
            </Box>

                    {isOrderEditable && (
                        <Box px={3} pt={3} pb={0}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 1.5,
                                    "@media screen and (max-width: 47.9375em)": {
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        gap: 2
                                    }
                                }}
                            >
                                <Subheading
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)": {
                                            display: "none",
                                        }
                                    }}
                                >
                                    {t("orderlines")}
                                </Subheading>
                                <Shbutton
                                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                    variant="contained"
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => setConfig(config => ({ ...config, is_editting: false }))}
                                >
                                    {t("edit")}
                                </Shbutton>
                            </Box>
                        </Box>
                    )}

                    {hasReadPricePermission && ((orderTotals.sellingPrice -orderTotals.sellingPriceExVat) > 0) &&
                        <Box
                            sx={{
                                mx:3,
                                mt:3,
                                p:2,
                                py:1.5,
                                borderRadius:4,
                                background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                            }}
                        >
                            <Text bold>
                                {t("total_order_price")}
                            </Text>
                            <Box 
                                sx={{ 
                                    mt:1.5,
                                    display:"flex", 
                                    justifyContent:"space-between", 
                                    alignItems:"center" 
                                }}
                            >
                                <Text light>
                                    {t("total_price_ex_vat")}
                                </Text>
                                
                                <Text bold>
                                    {formatPrice(t, orderTotals.sellingPriceExVat)}
                                </Text>
                            </Box>
                            {(orderTotals.sellingPrice -orderTotals.sellingPriceExVat) > 0 &&
                                <Box 
                                    sx={{ 
                                        display:"flex", 
                                        justifyContent:"space-between", 
                                        alignItems:"center" 
                                    }}
                                >
                                    <Text light>
                                        {t("total_vat")}
                                    </Text>
                                    
                                    <Text>
                                        {formatPrice(t, (orderTotals.sellingPrice -orderTotals.sellingPriceExVat))}
                                    </Text>
                                </Box>
                            }
                            <Box 
                                sx={{ 
                                    mt:0.5,
                                    pt:0.5,
                                    borderTop:`1px solid ${ theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[200]}`,
                                    display:"flex", 
                                    justifyContent:"space-between", 
                                    alignItems:"center" 
                                }}
                            >
                                <Text>
                                    {t("total_price")}
                                </Text>
                                
                                <Text bold>
                                    {formatPrice(t, (orderTotals.sellingPrice))}
                                </Text>
                            </Box>
                        </Box>
                    }
                    <Box sx={{ mt: (hasReadPricePermission && ((orderTotals.sellingPrice -orderTotals.sellingPriceExVat) > 0)) ? -1 : 0, }}>
                        <OrderlinesComp
                            orderLinesData={orderLinesData}
                            isLoadingLines={isLoadingLines}
                            getProductDetails={getProductDetails}
                            orderId={props.data?.id}
                            orderState={props.data?.state}
                            hideTitle={isOrderEditable}
                            // getVirtualProductDetails={getVirtualProductDetails}
                        />
                    </Box>
                    <OrderEvents orderId={props.data?.id} sx={{ pb: props.data?.returns?.length > 0 ? 3 : 0 }} />
                    {props.data?.returns?.length > 0 && (
                        <Box
                            sx={{
                                px:3,
                            }}
                        >
                            <Subheading
                                sx={{
                                    pb:1.5,
                                }}
                            >
                                {t("returns")}
                            </Subheading>
                            <Box
                                sx={{
                                    display:"flex",
                                    flexDirection:'column',
                                    gap:1.5,
                                }}
                            >
                                {props.data.returns.map((item,index) => (
                                    <ButtonBase
                                        onClick={() => {
                                            getReturnDetails(item.id);
                                        }}
                                        sx={{
                                            borderRadius:3,
                                            px:2,
                                            py:1.5,
                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                            border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                            boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                            display:"flex",
                                            flexDirection:'column',
                                            alignItems:'flex-start',
                                            justifyContent:'flex-start',
                                        }}
                                    >
                                        <Text bold>
                                            {item.reference}
                                        </Text>
                                        <Text light>
                                            {item.amountOfLines} {t("lines")}
                                        </Text>
                                    </ButtonBase>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        flex:1,
                        minWidth:0,
                        "@media screen and (max-width: 47.9375em)" : {
                            display:'none',
                        }
                    }}
                >
                    {props.data?.orderFieldValues?.some(
                        ofv => ofv.value !== null && ofv.value !== "" && ofv.value !== undefined
                    ) && (
                        <Box
                            sx={{
                                pr:3,
                                pt:3,
                            }}
                        >
                            <Subheading>{t("custom_fields_orders")}</Subheading>
                            <Box
                                sx={{
                                    display:"flex",
                                    flexDirection:"column",
                                    gap:1,
                                    mt:1,

                                }}
                            >
                                {props.data?.orderFieldValues
                                    .filter(ofv =>
                                        ofv.value !== null &&
                                        ofv.value !== "" &&
                                        ofv.value !== undefined
                                    )
                                    .map((ofv, ofvidex) => {
                                        
                                        return (
                                            booleanValueTypes.includes(ofv.orderfield.valueType) ?
                                            <Box
                                                key={"ofv" + ofvidex}
                                                sx={{
                                                    height:24,
                                                    border:`1px solid ${toBoolean(ofv.value) ? colors.green[400] : colors.red[400]}`,
                                                    background: toBoolean(ofv.value) ? colors.green[100] : colors.red[100],
                                                    borderRadius:3,
                                                    display:'flex',
                                                    alignItems:"center",
                                                    justifyContent:"space-between",
                                                    px:1,
                                                    "@media screen and (max-width: 47.9375em)" : {
                                                        flexWrap:"wrap",
                                                        height:"unset",
                                                        py:0.5,
                                                    }
                                                }}
                                            >
                                                <Text>
                                                    {ofv.orderfield?.name}
                                                </Text>

                                                <Box
                                                    sx={{
                                                        color: toBoolean(ofv.value) ? colors.green[400] : colors.red[400],
                                                        fontSize:14,
                                                        fontWeight:700,
                                                        fontFamily:"Roboto,sans-serif",
                                                        lineHeight:"16px",
                                                    }}
                                                >
                                                    {t(toBoolean(ofv.value) ? "yes" : 'no')}
                                                </Box>
                                            </Box>
                                            :
                                            <Box
                                                key={"ofv"  + ofvidex}
                                                sx={{
                                                    height:24,
                                                    border:`1px solid ${theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[200]}`,
                                                    background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                                                    borderRadius:3,
                                                    display:'flex',
                                                    alignItems:"center",
                                                    justifyContent:"space-between",
                                                    px:1,
                                                    "@media screen and (max-width: 47.9375em)" : {
                                                        flexWrap:"wrap",
                                                        height:"unset",
                                                        py:0.5,
                                                    }
                                                }}
                                            >
                                                <Text light>
                                                    {ofv.orderfield?.name}
                                                </Text>

                                                <Text>
                                                    {ofv.value.toString()}
                                                </Text>
                                            </Box>

                                        )
                                    })
                                }
                            </Box>
                        </Box>
                    )}


                    <TimeLine
                        logData={logData}
                        isRelation
                        webshopType={props.data?.webshopcredentials?.webshop_type}
                    />
                </Box>

            {spStockLoading &&
                <SpStockDetails
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    productId={productId}
                />
            }
            
            {returnSpLoading &&
                <SpReturnDetails
                    returnId={returnId}
                    isExtended={returnIsExtended}
                    setIsExtended={setReturnIsExtended}
                    // data={pickingJobData}
                />
            }

            <MSelectLabel
                open={labelsModal.open}
                handleClose={() => setLabelsModal({ ...labelsModal, open: false })}
                labels={labelsModal.labels}
                type={labelsModal.type}
            />

        </Box>
    );
}

export default OrderLines;